import { Injectable } from '@angular/core';
import {
  ApiService,
  GetRequestArguments,
  PostRequestArguments,
  Refresh,
} from './api.service';
import { CacheService } from 'ionic-cache';
import {
  HttpErrorResponse,
  HttpParams,
  HttpClient,
} from '@angular/common/http';
import { BehaviorSubject, Observable, forkJoin, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { FeedbackService } from './feedback.service';
import { NavController } from '@ionic/angular';
import { ProjectService } from './project.service';
import { CurrencyService } from './currency.service';
import { UserService } from './user.service';
import { conditionalFileRequiredExpenses } from '../validators/conditional-file-required-validator';
import { fileTypeValidator } from '../validators/fileTypeValidator';
import { fileSizeValidator } from '../validators/fileSizeValidator';
import { CustomizationService } from 'src/app/services/customization.service';
import { ApiRow } from '../util/interfaces/Review';
import { groupByUserCurrencyState, groupFlatRowsByUser } from '../util/grouping';

@Injectable({
  providedIn: 'root',
})
export class ExpenseService {
  public $expenses = new BehaviorSubject<any>([]);
  public $summary = new BehaviorSubject<any>([]);
  public $journals = new BehaviorSubject([]);
  public $journalIds = new BehaviorSubject([]);
  public $form = new BehaviorSubject(undefined);

  public $allJournals = new BehaviorSubject([]);

  public $itemStates = new BehaviorSubject([]);
  public $itemCategories = new BehaviorSubject([]);
  public $reviewFilter = new BehaviorSubject(undefined);
  public $expenseFilter = new BehaviorSubject(undefined);
  public $journalFilter = new BehaviorSubject(undefined);

  public $review = new BehaviorSubject([]);
  public $reviewing = new BehaviorSubject([]);
  public $approved = new BehaviorSubject([]);

  public $editingItemCategory = new BehaviorSubject({});
  public $newItemCategory = new BehaviorSubject({});

  public $countries = new BehaviorSubject([]);
  public $tenantCountry = new BehaviorSubject([]);

  public $vatRates = new BehaviorSubject([]);

  public $editing = new BehaviorSubject({});
  public $new = new BehaviorSubject({});

  private url = 'expense';
  private groupKey = 'expense';
  private hour = 60 * 60;
  public APITokens;
  constructor(
    private api: ApiService,
    private cache: CacheService,
    private formBuilder: UntypedFormBuilder,
    private feedbackService: FeedbackService,
    private projectService: ProjectService,
    private currencyService: CurrencyService,
    private navController: NavController,
    private userService: UserService,
    private customizationService: CustomizationService
  ){}

  public updateReviewFilter(filter, refresh: Refresh = 'expired') {
    this.api.updateObservable(filter, this.$reviewFilter);
    return this.loadReview(refresh, filter);
  }

  public updateExpenseFilter(filter, refresh: Refresh = 'expired') {
    this.api.updateObservable(filter, this.$expenseFilter);
    return this.loadReport(refresh, filter);
  }

  public updateJournalFilter(filter, refresh: Refresh = 'expired') {
    this.api.updateObservable(filter, this.$journalFilter);
    return this.loadJournal(refresh, filter);
  }

  private mapParams(filter: any) {
    let params = new HttpParams();
    if (filter !== undefined && filter !== null) {
      for (const key of Object.keys(filter)) {
        if (Array.isArray(filter[key])) {
          params = params.set(key, filter[key].map((entry) => entry.id).join(','));
        } else {
          params = params.set(key, filter[key]);
        }
      }
    }
    return params;
  }

  private buildArgs(
    fil: any,
    defaultFilter: any,
    url1,
    url2,
    refresh: Refresh,
    method: string = 'GET'
  ) {
    let filter = this.checkFilter(fil, defaultFilter);
    let params = this.mapParams(filter);
    let args = null;
    if (method == 'GET') {
      args = new GetRequestArguments({
        url: [url1, url2],
        cacheGroup: this.groupKey,
        refresh,
        params,
      });
    } else {
      const wantsBlob = (url2 === 'expenseZip' && fil?.['X-Download'] === 'true');
      args = new PostRequestArguments({
        url: [url1, url2],
        object: params,
        responseType: wantsBlob
          ? ('blob' as any)
          : (fil?.['X-Download'] === 'true' ? 'text' : 'json')
      });

      if (wantsBlob) {
        (args as any).observe = 'response';
      }
    }
    return args;
  }

  private checkFilter(fil, defaultFilter) {
    let filter;
    if (fil) {
      filter = fil;
    } else if (defaultFilter !== undefined) {
      filter = defaultFilter;
    }
    return filter;
  }

  public loadReport(refresh: Refresh = 'expired', fil?) {
    if (fil) {
      const args = this.buildArgs(
        fil,
        this.$expenseFilter.value,
        this.url,
        'filter',
        refresh
      );
      const request = this.api.cachedGetRequest(args);
      request.subscribe((expenses) => {
        const grouped = groupFlatRowsByUser(expenses as ApiRow[]);
        this.$approved.next(grouped);
      });
      return request;
    }
  }


public loadReview(refresh: Refresh = 'expired', fil?) {
  if (fil) {
    const args = this.buildArgs(
      fil,
      this.$reviewFilter.value,
      this.url,
      'filter',
      refresh
    );
    const request = this.api.cachedGetRequest(args);
    request.subscribe((expenses) => {
      // expenses: ApiRow[] (server rows)
      const grouped = groupByUserCurrencyState(expenses as ApiRow[]);
      this.$review.next(grouped);
    });
    return request;
  }
}


  public loadJournal(refresh: Refresh = 'expired', fil?) {
    const args = this.buildArgs(
      fil,
      this.$journalFilter.value,
      this.url,
      'journal',
      refresh
    );
    const request = this.api.cachedGetRequest(args);
    request.subscribe((journals) => {
      this.$journals.next(journals);
    });
    return request;
  }

  public async invalidateJournalIds() {
    const url = await this.api.getCacheUrl([this.url, 'journal_ids_by_date_range']);
    this.cache.removeItem(url);
  }

  public loadJournalIdsByDateRange(refresh: Refresh = 'expired', fil?) {
    const args = this.buildArgs(
      fil,
      this.$journalFilter.value,
      this.url,
      'journal_ids_by_date_range',
      refresh
    );
    const request = this.api.cachedGetRequest(args);
    request.subscribe((journalIds) => {
      this.$journalIds.next(journalIds);
    });
    return request;
  }

  public async loadForReview($userId, refresh: Refresh = 'expired') {
    this.$reviewFilter.subscribe((filter) => {
      if (!filter) {
        this.navController.navigateBack('expenses/review');
      } else {
        let params = new HttpParams();
        for (const key of Object.keys(filter)) {
          if (key !== 'users') {
            params = params.set(
              key,
              filter[key].map((entry) => entry.id)
            );
          }
        }
        const args = new GetRequestArguments({
          url: [this.url, 'filter', $userId],
          cacheGroup: this.groupKey,
          refresh,
          params,
        });
        const request = this.api.cachedGetRequest(args);
        request.subscribe((expenses) => {
          this.api.updateObservable(expenses, this.$reviewing);
        });
      }
    });
  }

  public load(refresh: Refresh = 'expired') {
    return new Promise<void>((resolve, reject) => {
      forkJoin([
        this.loadExpenses(refresh),
        this.loadSummary(refresh),
      ]).subscribe({
        next: () => {
          resolve();
        },
        error: (error) => {
          reject();
        },
      });
    });
  }

  public loadVatRates(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'vat_rates'],
      cacheGroup: this.groupKey,
      ttl: 20,
      refresh,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe((expenses) => {
      const updatedExpenses = expenses.map((expense) => ({
        ...expense,
        display_name: expense.display_name || expense.name,
      }));
      this.api.updateObservable(updatedExpenses, this.$vatRates);
    });
    return call;
  }

  public loadVatRatesCountry(refresh: Refresh = 'expired', $id) {
    const args = new GetRequestArguments({
      url: [this.url, 'vat_rates', $id],
      cacheGroup: this.groupKey,
      ttl: 20,
      refresh,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe((expenses) => {
      const updatedExpenses = expenses.map((expense) => ({
        ...expense,
        display_name: expense.display_name || expense.name,
      }));
      this.api.updateObservable(updatedExpenses, this.$vatRates);
    });
    return call;
  }

  public loadAll(refresh: Refresh = 'expired') {
    return new Promise<void>((resolve, reject) => {
      forkJoin([
        this.loadItemCategories(refresh),
        this.loadItemStates(refresh),
        this.loadCountries(refresh),
      ]).subscribe({
        next: () => {
          resolve();
        },
        error: (error) => {
          reject();
        },
      });
    });
  }

  public loadExpenses(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: this.url,
      cacheGroup: this.groupKey,
      ttl: 20,
      refresh,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe({
      next: (expenses) => {
        this.api.updateObservable(expenses, this.$expenses);
      },
      error: (error) => {
        this.feedbackService.invalidScopes(error);
      },
    });
    return call;
  }

  public loadSummary(refresh: Refresh = 'expired', $from = null, $to = null) {
    let params = new HttpParams();
    $from !== null ? (params = params.set('from', $from)) : {};
    $to !== null ? (params = params.set('to', $to)) : {};
    const args = new GetRequestArguments({
      url: [this.url, 'summary'],
      cacheGroup: this.groupKey,
      ttl: 20,
      refresh,
      params,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe({
      next: (summary) => {
        this.api.updateObservable(summary, this.$summary);
      },
      error: (error) => {
        this.feedbackService.invalidScopes(error);
      },
    });
    return call;
  }

  public loadExpenseForEdit(expenseId) {
    const args = new GetRequestArguments({
      url: [this.url, expenseId],
      cacheGroup: undefined,
    });
    this.api.cachedGetRequest(args).subscribe(
      (expense) => {
        const form = this.createExpenseForm();
        this.fillExpenseForm(form, expense);
        this.api.updateObservable(form, this.$editing);
      },
      (error) => {
        this.feedbackService.standardError(error, 4000, 'top').then(() => {
          this.navController.navigateBack(['/', 'expenses']);
        });
      }
    );
  }

  public loadItemStates(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'itemStates'],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      refresh,
      delayed: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe((itemStates) => {
      this.api.updateObservable(itemStates, this.$itemStates);
    });
    return call;
  }

  public loadItemCategories(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'itemCategory'],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      refresh,
      delayed: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe((itemCategories) => {
      this.api.updateObservable(itemCategories, this.$itemCategories);
    });
    return call;
  }

  public loadItemCategoriesForUser(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'itemCategory', 'user'],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      refresh,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe((itemCategories) => {
      this.api.updateObservable(itemCategories, this.$itemCategories);
    });
    return call;
  }

  public loadItemCategory(itemCategoryId) {
    const args = new GetRequestArguments({
      url: [this.url, 'itemCategory', itemCategoryId],
      cacheGroup: undefined,
    });
    this.api.cachedGetRequest(args).subscribe({
      next: (itemCategory) => {
        const form = this.createItemCategoryForm();
        this.fillItemCategoryForm(form, itemCategory);
        this.api.updateObservable(form, this.$editingItemCategory);
      },
      error: (error) => {
        this.feedbackService.standardError(error, 4000, 'top').then(() => {
          this.navController.navigateBack(['/', 'expenses', 'itemCategories']);
        });
      },
    });
  }

  loadCountries(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: ['countries'],
      cacheGroup: this.groupKey,
      refresh,
      ttl: this.hour * 24,
      delayed: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe((countries) => {
      this.api.updateObservable(
        countries.find((c) => c.is_tenant_country === true),
        this.$tenantCountry
      );
      this.api.updateObservable(countries, this.$countries);
    });
    return call;
  }

  public postItemCategory(itemCategory) {
    const args = new PostRequestArguments({
      url: [this.url, 'itemCategory'],
      object: itemCategory,
    });
    const call = this.api.postRequest(args);
    call.subscribe({
      next: (itemCategories) => {
        this.$editingItemCategory.next(undefined);
        this.api.updateObservable(itemCategories, this.$itemCategories);
        this.api.getCacheUrl([this.url, 'itemCategory']).then((url) => {
          this.cache.saveItem(url, itemCategories, this.groupKey, 60 * 60 * 24);
        });
      },
      error: (error) => {
        this.feedbackService.standardError(error);
      },
    });
    return call;
  }

  public deleteItemCategory(itemCategoryId) {
    const args = new PostRequestArguments({
      url: [this.url, 'itemCategory'],
      object: itemCategoryId,
    });
    const call = this.api.deleteRequest(args);
    call.subscribe({
      next: (id) => {
        if (typeof id === 'string') {
          const index = this.$itemCategories.value.findIndex(
            (c) => c.id === parseInt(id, 10)
          );
          this.$itemCategories.value.splice(index, 1);
        }
        this.feedbackService.message(
          'Expense Item Category was successfully deleted',
          'Success'
        );
      },
      error: (error) => {
        this.feedbackService.standardError(error);
      },
    });
    return call;
  }

  public createExpenseForm() {
    return this.formBuilder.group({
      amount: [undefined, [Validators.required]],
      company: [false, [Validators.required]],
      country_id: [undefined, []],
      country: [undefined, [Validators.required]],
      created_at: [undefined, []],
      currency: [undefined, [Validators.required]],
      date: [[], [Validators.required]],
      description: [undefined, []],
      exchange_amount: [undefined, []],
      exchange_rate: [undefined, []],
      exchange_rate_suggestion: [undefined, []],
      file: [undefined, []],
      file_name: [undefined, []],
      id: [undefined, []],
      issuer: [undefined, []],
      //project_id: [undefined, []],
      project: [undefined, [Validators.required]],
      item_state: [undefined, []],
      item_category: [undefined, [Validators.required]],
      updated_at: [undefined, []],
      user_id: [undefined, []],
      vat: [
        undefined,
        [Validators.required, Validators.min(0.0), Validators.max(99.9)],
      ],
      approved_at: [undefined, []],
      submitted_at: [undefined, []],
    });
  }

  public fillExpenseForm(form, expense) {
    if (expense.id) {
      form.controls.amount.patchValue(parseFloat(expense.quantity));
      form.controls.company.patchValue(!!Number(expense.company));
      form.controls.country_id.patchValue(parseInt(expense.country_id));
      form.controls.country.patchValue(
        this.$countries.value.find((c) => c.id === expense.country_id)
      );
      form.controls.created_at.patchValue(expense.created_at);
      form.controls.currency.patchValue(expense.currency);
      form.controls.date.patchValue([expense.date]);
      form.controls.description.patchValue(expense.description);
      form.controls.exchange_amount.patchValue(expense.exchange_amount);
      form.controls.exchange_rate.patchValue(expense.exchange_rate);
      form.controls.exchange_rate_suggestion.patchValue(
        expense.exchange_rate_suggestion
      );
      form.controls.file_name.patchValue(expense.file);
      form.controls.id.patchValue(expense.id);
      form.controls.issuer.patchValue(expense.issuer);
      //form.controls.project_id.patchValue(expense.project_id);
      this.projectService.$projects.subscribe(() => {
        form.controls.project.patchValue(
          this.projectService.$projects.value.find(
            (c) => c.id === parseInt(expense.project_id)
          )
        );
      });
      form.controls.item_state.patchValue(expense.item_state);
      form.controls.item_category.patchValue(expense.item_category);
      form.controls.updated_at.patchValue(expense.updated_at);
      form.controls.user_id.patchValue(expense.user_id);
      form.controls.vat.patchValue(expense.vat_rate);
      if (!form.value.item_state.edit) {
        form.disable();
      }
      form.controls.approved_at.patchValue(expense.approved_at);
      form.controls.submitted_at.patchValue(expense.submitted_at);
    }
  }

  public updateDynamicFileValidator(form, file) {
    const fileControl = form.get('file');

    if (!form.value.file_name && !form.value.file) {
      fileControl.setValidators([
        conditionalFileRequiredExpenses(form, this.$itemCategories.getValue()),
        fileSizeValidator(file, 2), // 2MB file size limit
      ]);
    } else if (file.type === 'application/pdf') {
      fileControl.setValidators([fileSizeValidator(file, 2)]);
    } else {
      fileControl.setValidators([fileTypeValidator(file)]);
    }
    fileControl.updateValueAndValidity();
    this.$form.next(form);
  }

  public createExpenseFormData(expense, file) {
    const formData = this.api.createFormData(expense);
    if (file) {
      formData.append('attachment', file, file.name);
    }
    return formData;
  }

  public createItemCategoryForm() {
    return this.formBuilder.group({
      created_at: [undefined, []],
      default_value: [undefined, []],
      deleted_at: [undefined, []],
      description: [undefined, []],
      id: [NaN, []],
      max: [undefined, []],
      name: [undefined, []],
      additional_data: [false, []],
      receipt: [false, []],
      bexio_transfer_account: [undefined, []],
      bexio_bank_account: [undefined, []],
      bexio_expense_account: [undefined, []],
      bexio_external_account: [undefined, []],
      bexio_account_tax: [undefined, []],
      bexio_account_vat: [undefined, []],
      updated_at: [undefined, []],
      active: [false, []],
      default_vat: [undefined, []],
      email_notifications: [false, []],
    });
  }

  public fillItemCategoryForm(form, item_category) {
    if (item_category !== undefined && item_category !== null) {
      form.controls.created_at.patchValue(item_category.created_at);
      form.controls.default_value.patchValue(item_category.default_value);
      form.controls.deleted_at.patchValue(item_category.deleted_at);
      form.controls.description.patchValue(item_category.description);
      form.controls.id.patchValue(parseInt(item_category.id, 10));
      form.controls.max.patchValue(item_category.max);
      form.controls.name.patchValue(item_category.name);
      form.controls.bexio_transfer_account.patchValue(
        item_category.bexio_transfer_account
      );
      form.controls.bexio_external_account.patchValue(
        item_category.bexio_external_account
      );
      form.controls.bexio_expense_account.patchValue(
        item_category.bexio_expense_account
      );
      form.controls.bexio_bank_account.patchValue(
        item_category.bexio_bank_account
      );
      form.controls.bexio_account_tax.patchValue(
        item_category.bexio_account_tax
      );
      form.controls.bexio_account_vat.patchValue(
        item_category.bexio_account_vat
      );
      form.controls.updated_at.patchValue(item_category.updated_at);
      form.controls.active.patchValue(item_category.active);

      item_category.receipt = Boolean(item_category.receipt);
      form.controls.receipt.patchValue(item_category.receipt);
      item_category.email_notifications = Boolean(
        item_category.email_notifications
      );
      form.controls.email_notifications.patchValue(
        item_category.email_notifications
      );
      form.controls.default_vat.patchValue(item_category.default_vat);
    }
  }

  public createItemCategoryFormData(item_category) {
    return this.api.createFormData(item_category);
  }

  public readDocument(file) {
    const formData = new FormData();
    formData.append('document', file);
    const args = new PostRequestArguments({
      url: [this.url, 'read'],
      object: formData,
    });
    const request = this.api.postRequest(args);
    request.subscribe((res) => {
      for (const key of Object.keys(res)) {
        if (res[key] !== null) {
          if (key === 'amount') {
            this.$form.value.controls.amount.patchValue(parseFloat(res[key]));
          } else if (key === 'date') {
            this.$form.value.controls.date.patchValue([res[key]]);
          } else if (key === 'currency') {
            this.currencyService.$allCurrencies.subscribe((currency) => {
              const curr = currency.find(
                (c) => c.shortcode.toLowerCase() === res[key].toLowerCase()
              );
              if (curr) {
                this.$form.value.controls.currency.patchValue(curr);
              }
            });
          } else if (key === 'vat') {
            this.$form.value.controls.vat.patchValue(res[key]);
          } else {
            let text = this.$form.value.value.description;
            if (text) {
              text += '/n' + key + ': ' + res[key];
            } else {
              text = res[key];
            }
            this.$form.value.controls.description.patchValue(text);
          }
        }
      }
    });
    return request;
  }

  public post(expense: FormData) {
    const args = new PostRequestArguments({
      url: this.url,
      object: expense,
    });
    const call = this.api.postRequest(args);
    call.subscribe({
      next: (expenses) => {
        this.$editing.next(undefined);
        this.api.updateObservable(expenses, this.$expenses);
        this.api.getCacheUrl(this.url).then((url) => {
          this.cache.saveItem(url, expenses, this.groupKey, 60);
        });
      },
      error: (error) => {
        console.log(error);
      },
    });
    return call;
  }

  getFiltered(filter, refresh: Refresh = 'expired', userId: number = null) {
    let params = new HttpParams();
    for (const key of Object.keys(filter)) {
      params = params.set(
        key,
        filter[key].map((entry) => entry.id)
      );
    }
    const args = new GetRequestArguments({
      url: userId ? [this.url, 'filter', userId] : [this.url, 'filter'],
      cacheGroup: this.groupKey,
      refresh,
      params,
    });
    return this.api.cachedGetRequest(args);
  }

  swipe(expense, transition_name, userFacing = false) {
    const args = new GetRequestArguments({
      url: [this.url, expense.id, 'transition', transition_name],
      cacheGroup: undefined,
    });
    const request = this.api.cachedGetRequest(args);
    request.subscribe({
      next: (data) => {
        if (userFacing) {
          this.loadExpenses('all');
        } else {
          this.loadForReview(this.$reviewing.value[0]?.user_id, 'all');
          // pass the current review filter to keep grouping + filters in sync
          this.loadReview('all', this.$reviewFilter.value);
        }
      },
      error: (error) => {
        console.log('error:');
        console.log(error);
      },
    });
    return request;
  }


  swipeMultiple(expenseIds, fromState, transition_name) {
    let params = new HttpParams();
    params = params.append('expenseIds', expenseIds.join(','));

    const args = new GetRequestArguments({
      url: [this.url, 'multi_transition', fromState, transition_name],
      cacheGroup: undefined,
      params,
    });
    const request = this.api.cachedGetRequest(args);
    request.subscribe(() => {
      this.loadForReview(this.$reviewing.value[0]?.user_id, 'all');
      this.loadReview('all', this.$reviewFilter.value);
    });
    return request;
  }

  invalidateExpense() {
    this.cache.removeItem(this.url);
  }

  delete(expense) {
    const args = new PostRequestArguments({
      url: this.url,
      object: expense,
    });
    const call = this.api.deleteRequest(args);
    call.subscribe({
      next: (id) => {
        if (typeof id === 'string') {
          const index = this.$expenses.value.findIndex(
            (c) => c.id === parseInt(id, 10)
          );
          this.$expenses.value.splice(index, 1);
        }
        this.feedbackService.message(
          'Expense was successfully deleted',
          'Success'
        );
      },
      error: (error) => {
        this.feedbackService.standardError(error);
      },
    });
    return call;
  }

  getForUser(fromDate, toDate, userId, refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'review', userId],
      cacheGroup: this.groupKey,
      refresh,
      params: new HttpParams().set('from', fromDate).set('to', toDate),
    });
    return this.api.cachedGetRequest(args);
  }

  updateReview(expense: FormData) {
    const args = new PostRequestArguments({
      url: [this.url, 'review'],
      object: expense,
    });
    return this.api.postRequest(args);
  }

  getAttachment(expenseId) {
    const args = new GetRequestArguments({
      url: [this.url, 'attachment', expenseId],
      ttl: 60 * 4,
      cacheGroup: this.groupKey
    });
    return this.api.cachedGetRequest(args);
  }

  createReport(fil, download=false) {
    fil['X-Download'] = download? 'true' : null;
    const args = this.buildArgs(
      fil,
      this.$expenseFilter.value,
      this.url,
      'report',
      'all',
      'POST'
    );
    return this.api.postRequest(args);
  }

  createExpenseZip(fil, download=false) {
    fil['X-Download'] = download? 'true' : null;
    const args = this.buildArgs(
      fil,
      this.$expenseFilter.value,
      this.url,
      'expenseZip',
      'all',
      'POST'
    );
    return this.api.postRequest(args);
  }

  generateJournalID(fil, selectedState): Observable<any> {
    const args = this.buildArgs(
      fil,
      this.$expenseFilter.value,
      this.url,
      'journalID',
      'all',
      'POST'
    );
    const request = this.api.postRequest(args);
    request.subscribe({
      next: (res) => {
        if (this.hasBexioToken() && selectedState == 1) {
          this.prepareExpenses(fil);
        }
      },
      error: (error) => {
        this.handleError(error);
      },
    });
    return request;
  }

  private handleError(error: HttpErrorResponse): Observable<any> {
    this.feedbackService.standardError(error);
    console.log('in error');
    return throwError(() => new Error(error.error.message));
  }

  bookBexioExpenses(fil) {
    const args = this.buildArgs(
      fil,
      this.$expenseFilter.value,
      'invoice',
      'createBexioInvoice',
      'all',
      'POST'
    );
    const call = this.api.postRequest(args);
    call.subscribe({
      next: (approveExpenses) => {
        this.api.getCacheUrl(['invoice', 'createBexioInvoice']).then();
        // Display the warning that the attachment was not sent, approveExpenses[1] contains the reply of the post attachment method
        if (approveExpenses[1].length > 0) {
          this.feedbackService.optionMessage(
            approveExpenses[1].join('<br>'),
            'Attachment Warning',
            20000,
            'bottom'
          );
        }
      },
      error: (error) => {
        let msg = '';
        if (error.status == 500) {
          this.feedbackService.standardError(error);
        } else {
          for (const err of error.error[0]) {
            if (err['contains_error']) {
              msg = msg + err['data'] + '<br>';
            }
          }
          this.feedbackService.optionMessage(
            msg,
            'Failed booking expenses in Bexio!'
          );
        }
      },
    });
    return call;
  }

  payExpenses(fil) {
    const args = this.buildArgs(
      fil,
      this.$expenseFilter.value,
      this.url,
      'payExpenses',
      'all',
      'POST'
    );
    const request = this.api.postRequest(args);
    request.subscribe({
      next: (res) => {
        this.$approved.next(undefined);
        this.feedbackService.message(res['message'], 'Success');
      },
      error: (error) => {
        this.feedbackService.standardError(error);
      },
    });
    return request;
  }

  prepareExpenses(fil) {
    const args = this.buildArgs(
      fil,
      this.$expenseFilter.value,
      this.url,
      'prepareExpenses',
      'all',
      'POST'
    );
    const request = this.api.postRequest(args);
    request.subscribe({
      next: (res) => {
        this.$approved.next(undefined);
        this.feedbackService.message(res['message'], 'Success');
      },
      error: (error) => {
        this.feedbackService.standardError(error);
      },
    });
    return request;
  }

  finalizeExpenses(fil) {
    const args = this.buildArgs(
      fil,
      this.$expenseFilter.value,
      this.url,
      'finalizeExpenses',
      'all',
      'POST'
    );
    const request = this.api.postRequest(args);
    request.subscribe({
      next: (res) => {
        this.$approved.next(undefined);
        this.feedbackService.message(res['message'], 'Success');
      },
      error: (error) => {
        this.feedbackService.standardError(error);
      },
    });
    return request;
  }

  payBexioAPI(fil): Observable<any> {
    const args = this.buildArgs(
      fil,
      this.$expenseFilter.value,
      this.url,
      'payBexioAPI',
      'all',
      'POST'
    );
    const request = this.api.postRequest(args);
    request.subscribe({
      next: (res) => {
        this.feedbackService.message(
          'Payment was successfully submitted to Bexio!',
          'Success'
        );
        this.payExpenses(fil);
      },
      error: (error) => {
        this.feedbackService.message(
          error?.error?.message,
          'Payment Failed!',
          4000,
          'bottom'
        );
      },
    });
    return request;
  }

  download() {
    const args = new GetRequestArguments({
      url: [this.url, 'download'],
      cacheGroup: undefined,
    });
    return this.api.cachedGetRequest(args);
  }

  summary(
    year: number = new Date().getFullYear(),
    refresh: Refresh = 'expired'
  ) {
    const args = new GetRequestArguments({
      url: [this.url, 'summary'],
      cacheGroup: this.groupKey,
      refresh,
      ttl: this.hour * 24,
    });
    return this.api.cachedGetRequest(args);
  }

  public loadSepa(filter, refresh: Refresh = 'expired') {
    let params = new HttpParams();
    for (const key of Object.keys(filter)) {
      params = params.set(
        key,
        filter[key].map((entry) => entry.id)
      );
    }
    const args = new GetRequestArguments({
      url: [this.url, 'sepa'],
      cacheGroup: undefined,
      refresh,
      params,
    });
    //this.api.clearCacheGroup(this.groupKey);
    const request = this.api.cachedGetRequest(args);
    request.subscribe({
      next: (data) => {
        this.api.downloadFile(
          data,
          'text/xml',
          'SEPA_' +
            filter['currencies'][0]['shortcode'] +
            '_' +
            filter['date'][0]['id'] +
            '_' +
            filter['date'][1]['id'] +
            '.xml'
        );
      },
      error: (error) => {
        console.log(error);
      },
    });

    return request;
  }

  hasBexioToken(): boolean {
    const tokens = this.customizationService.$apiTokens.getValue();
    if (!tokens) {
      return false;
    }

    return tokens.some(
      t => t.key === 'bexio' && t.active === 1
    );
  }
}
